import styled from '@emotion/styled';
import Comps from '@owenscorning/react-library';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ContractorSearch from './contractorSearch';
import CTAButton from '../../../CTAButton';
import RadioButton from '../../../ComponentLibrary/input-elements/oc-radio-button';
import BackButton from '../../shared/BackButton';
import { EMAIL_REGEX } from '../../shared/utils';
import AddressVerificationModal from '../helpers/address-verification-modal';
import { incrementStep, setFormData, submitClaim, verifyAddress } from '../redux/action';

const Subscript = styled.a`
  cursor: pointer;
  display: block;
  font-size: 14px;
  margin: 0;
  padding-top: 1.5rem;
  text-align: center;
  text-decoration: underline;
  width: 100%;
`;

const TextInput = Comps.Connect.FormElements.Text;
const { AddressGroup } = Comps.Connect.FormElements;
const { Spinner } = Comps.Connect;

const PropertyOwnerInvite = ({ clearValues }) => {
  const dispatch = useDispatch();
  const { formData, metadata, isFetching, disableSubmitBtn } = useSelector(state => state);

  const claimByContractor = formData.propertyClaimedBy === 'CONTRACTOR';
  const { showManualEntry } = formData;

  const validated =
    showManualEntry &&
    formData.newContractorInfo.company &&
    formData.newContractorInfo.firstName &&
    formData.newContractorInfo.lastName &&
    formData.newContractorInfo.phone &&
    formData.newContractorInfo.phone.length === 12 &&
    formData.newContractorInfo.street &&
    formData.newContractorInfo.city &&
    formData.newContractorInfo.state &&
    formData.newContractorInfo.zip;

  const enableBtn = () => {
    if (claimByContractor) {
      return (
        (!disableSubmitBtn && formData.propertyOwnerInvite === 'No') ||
        (formData.propertyOwnerInvite === 'Yes' &&
          formData.propertyOwnerEmail &&
          EMAIL_REGEX.test(formData.propertyOwnerEmail))
      );
    }
    if (!showManualEntry) {
      return (
        (!disableSubmitBtn && formData.contractorInvite === 'No') ||
        (formData.contractorInvite === 'Yes' && (formData.contractorEmail || formData?.installingContractor))
      );
    }
    return (
      (!disableSubmitBtn && formData.contractorInvite === 'No') ||
      (formData.foundContractor && formData?.installingContractor) ||
      (formData.contractorInvite === 'Yes' && validated)
    );
  };

  return (
    <>
      <BackButton
        analytics={{
          'data-track': 'form-back',
          'data-track-form-name': 'Submit Claim',
          'data-track-form-step': 'step 5 - property owner invite',
        }}
        onClick={() => clearValues()}
        backText={"What's the problem?"}
      />
      <p>Before we get to the additional information we need to collect,</p>
      <h4 className="mb10 mt10">
        {claimByContractor
          ? 'As the contractor initiating this claim, would you like the homeowner to receive updates regarding their warranty claim?'
          : 'As the homeowner initiating this claim, would you like the contractor to receive updates regarding this warranty claim?'}
      </h4>

      <div
        onChange={event => {
          claimByContractor
            ? dispatch(setFormData({ propertyOwnerInvite: event.target.value }))
            : dispatch(
                setFormData({
                  contractorInvite: event.target.value,
                  ...(event.target.value === 'No' && { showManualEntry: false }),
                })
              );
        }}
      >
        <div className="radioContainer">
          <RadioButton
            name="invitePropertyOwner"
            value="Yes"
            checked={claimByContractor ? formData.propertyOwnerInvite === 'Yes' : formData.contractorInvite === 'Yes'}
          >
            <label>Yes</label>
          </RadioButton>
        </div>
        <div className="radioContainer">
          <RadioButton
            name="invitePropertyOwner"
            value="No"
            checked={claimByContractor ? formData.propertyOwnerInvite === 'No' : formData.contractorInvite === 'No'}
          >
            <label>No</label>
          </RadioButton>
        </div>
      </div>

      <div
        style={{
          display: (formData.propertyOwnerInvite || formData.contractorInvite) === 'Yes' ? 'block' : 'none',
        }}
      >
        {(formData.contractorInvite === 'Yes' || formData.propertyOwnerInvite === 'Yes') && (
          <>
            <h4>
              {claimByContractor
                ? 'What is the property owner’s email address?'
                : !showManualEntry && 'What is the contractor’s email address?'}
            </h4>

            <div>
              {claimByContractor && (
                <TextInput
                  name="propertyOwnerEmail"
                  value={formData.propertyOwnerEmail}
                  htmlAttributes={{
                    placeholder: 'email@address.com',
                  }}
                  onChange={value => {
                    dispatch(setFormData({ propertyOwnerEmail: value }));
                  }}
                />
              )}
              {!showManualEntry && !claimByContractor && (
                <>
                  <ContractorSearch />
                  <Subscript onClick={() => dispatch(setFormData({ showManualEntry: true }))}>
                    My contractor is not listed
                  </Subscript>
                </>
              )}
              {showManualEntry && !claimByContractor && (
                <>
                  <h4>What is the contractor’s information?</h4>
                  <div className="inputWrap">
                    <ContractorSearch />
                  </div>
                  {!formData.foundContractor && (
                    <>
                      <TextInput
                        name="company"
                        value={formData.newContractorInfo.company || ''}
                        htmlAttributes={{
                          placeholder: 'Company Name',
                        }}
                        onChange={value =>
                          dispatch(
                            setFormData({
                              newContractorInfo: {
                                ...formData.newContractorInfo,
                                company: value,
                              },
                            })
                          )
                        }
                      />

                      <div className="twoFields">
                        <div className="inputWrap mr10">
                          <TextInput
                            name="firstName"
                            value={formData.newContractorInfo.firstName || ''}
                            htmlAttributes={{
                              placeholder: 'First Name',
                            }}
                            onChange={value =>
                              dispatch(
                                setFormData({
                                  newContractorInfo: {
                                    ...formData.newContractorInfo,
                                    firstName: value,
                                  },
                                })
                              )
                            }
                          />
                        </div>

                        <div className="inputWrap ml10">
                          <TextInput
                            name="contractorEmail"
                            value={formData.newContractorInfo.lastName || ''}
                            htmlAttributes={{
                              placeholder: 'Lastname',
                            }}
                            onChange={value =>
                              dispatch(
                                setFormData({
                                  newContractorInfo: {
                                    ...formData.newContractorInfo,
                                    lastName: value,
                                  },
                                })
                              )
                            }
                          />
                        </div>
                      </div>

                      <AddressGroup
                        value={formData.newContractorInfo}
                        onChange={address =>
                          dispatch(
                            setFormData({
                              newContractorInfo: {
                                ...formData.newContractorInfo,
                                ...address,
                              },
                            })
                          )
                        }
                        suggestionEndpoint={`${MDMS_URL}/api/v1/smarty/suggestions`}
                        showComplaints={false}
                        country={formData.countrySelected}
                      />

                      <div>
                        <TextInput
                          name="phone"
                          value={formData.newContractorInfo.phone || ''}
                          htmlAttributes={{
                            placeholder: 'Phone Number',
                            pattern: '[0-9 -]*',
                            maxLength: 12,
                          }}
                          onChange={value =>
                            dispatch(
                              setFormData({
                                newContractorInfo: {
                                  ...formData.newContractorInfo,
                                  phone: value,
                                },
                              })
                            )
                          }
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {metadata?.pqsResponse?.message && <p className="colorRed">Error submitting claims, click next to try again</p>}

      <CTAButton
        onClick={() => {
          if (showManualEntry && !formData.foundContractor) {
            dispatch(verifyAddress('newContractorInfo'));
          } else {
            dispatch(submitClaim());
          }
        }}
        data-track="form-submit"
        data-track-form-name="Submit Claim"
        data-track-form-step="step 5 - property owner invite"
        element="button"
        text={
          isFetching ? (
            showManualEntry ? (
              <>
                <Spinner /> Loading
              </>
            ) : (
              <>
                <Spinner /> Submitting claim...
              </>
            )
          ) : (
            'Submit'
          )
        }
        type="submit"
        disabled={!enableBtn()}
      />
      <AddressVerificationModal fieldKey="newContractorInfo" />
    </>
  );
};

export default PropertyOwnerInvite;
